import React, { useEffect } from "react";
import PropTypes from "prop-types";
import {
  TextField,
  Box,
  Radio,
  RadioGroup,
  Typography,
  FormControlLabel
} from "@mui/material";
import { useTranslation } from "react-i18next";
import makeStyles from "@mui/styles/makeStyles";
import HTMLEditor from "../../components/HtmlEditor/HtmlEditor";
import { Tooltip } from "../../components/Tooltip";
import usePermissions from "../../redux/selectors/usePermissions";

const fields = {
  name: {
    value: "name",
    errorMessage: "Name is required",
    placeholder: "Widget Name"
  },
  description: {
    value: "description",
    placeholder: "Description"
  },
  isPrivate: {
    value: "isPrivate",
    placeholder: "isPrivate"
  },
  content: {
    value: "content",
    errorMessage: "Content is required"
  }
};

const useStyles = makeStyles(theme => ({
  textField: {
    marginTop: theme.spacing(3.5)
  },
  radioGroupRoot: {
    marginRight: `${theme.spacing(3)} !important` // important necessary to override deeply nested material UI radio button styles
  },
  radioGroupLabel: {
    marginLeft: "0 !important"
  }
}));

const PrivateWidgetField = ({
  customWidgetData,
  handleCustomWidgetDataChange,
  disabled,
  disableShared
}) => {
  const classes = useStyles();
  return (
    <RadioGroup
      aria-label="isPrivate"
      name="isPrivate"
      value={customWidgetData.isPrivate.value}
      onChange={handleCustomWidgetDataChange(fields.isPrivate.value)}
      row
    >
      <FormControlLabel
        value="false"
        disabled={disabled || disableShared}
        control={<Radio color="primary" />}
        label={<Typography variant="body2">{"Shared"}</Typography>}
        classes={{
          root: classes.radioGroupRoot,
          label: classes.radioGroupLabel
        }}
      />
      <FormControlLabel
        value="true"
        disabled={disabled}
        control={<Radio color="primary" />}
        label={<Typography variant="body2">{"Private"}</Typography>}
        classes={{
          root: classes.radioGroupRoot,
          label: classes.radioGroupLabel
        }}
      />
    </RadioGroup>
  );
};

const CreateCustomWidget = ({
  onChange,
  formSaveAttempted,
  widgetData,
  onFileAdd,
  ownPrivateWidget
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const permissions = usePermissions();
  const disableShared = !permissions.isAdmin;
  const isEditing = widgetData ? true : false;
  const [customWidgetData, setCustomWidgetData] = React.useState({
    id: { value: isEditing ? widgetData.id : "" },
    name: {
      value: isEditing ? widgetData.name : "",
      error: isEditing ? false : true,
      helperText: fields.name.errorMessage
    },
    description: {
      value: isEditing ? widgetData.description : "",
      error: false
    },
    isPrivate: {
      value: isEditing
        ? widgetData.isPrivate.toString()
        : disableShared
        ? "true"
        : "false",
      error: false
    },
    creatorId: isEditing ? widgetData.creatorId : "",
    content: {
      value: isEditing ? widgetData.content : "",
      error: isEditing ? false : true,
      helperText: fields.name.errorMessage
    }
  });

  const handleCustomWidgetDataChange = field => event => {
    const value = event.target ? event.target.value : event;
    setCustomWidgetData({
      ...customWidgetData,
      [field]: {
        ...customWidgetData[field],
        value,
        error: !value
      }
    });
  };

  const disableRadioGroup = () => {
    if (ownPrivateWidget && isEditing) return false;
    else if (isEditing && widgetData.creatorId === null) return true;
    else if (!isEditing) return false;
    else return true;
  };

  useEffect(() => {
    onChange(customWidgetData);
  }, [customWidgetData]);

  return (
    <Box display="flex" flexDirection="column" mt={0.5}>
      <TextField
        placeholder={fields.name.placeholder}
        variant="filled"
        fullWidth
        value={customWidgetData.name.value}
        onChange={handleCustomWidgetDataChange(fields.name.value)}
        className={classes.textField}
        error={formSaveAttempted && customWidgetData[fields.name.value].error}
        helperText={
          formSaveAttempted && customWidgetData[fields.name.value].error
            ? fields.name.errorMessage
            : ""
        }
        hiddenLabel
      />
      <TextField
        placeholder={fields.description.placeholder}
        variant="filled"
        fullWidth
        value={customWidgetData.description.value}
        onChange={handleCustomWidgetDataChange(fields.description.value)}
        className={classes.textField}
        hiddenLabel
      />
      {!disableRadioGroup() ? (
        <PrivateWidgetField
          customWidgetData={customWidgetData}
          handleCustomWidgetDataChange={handleCustomWidgetDataChange}
          disabled={false}
          disableShared={disableShared}
        />
      ) : (
        <Tooltip
          title={
            <Typography variant="body2" color="primary">
              {t("customWidget.tooltipTitle")}
            </Typography>
          }
        >
          <Box display="flex" flex={1}>
            <PrivateWidgetField
              customWidgetData={customWidgetData}
              handleCustomWidgetDataChange={handleCustomWidgetDataChange}
              disabled={true}
              disableShared={disableShared}
            />
          </Box>
        </Tooltip>
      )}
      <Box mt={0.5}>
        <HTMLEditor
          errorMessage={
            formSaveAttempted && customWidgetData[fields.content.value].error
              ? fields.content.errorMessage
              : ""
          }
          plugins={["lists", "link", "image", "media", "code", "fileUpload"]}
          value={customWidgetData.content.value}
          onChange={handleCustomWidgetDataChange(fields.content.value)}
          onFileAdd={onFileAdd}
        />
      </Box>
    </Box>
  );
};

CreateCustomWidget.propTypes = {
  /**
   * Called when the user makes a change to one of the fields in the create custom widget form
   */
  onChange: PropTypes.func,
  onFileAdd: PropTypes.func,
  hasError: PropTypes.bool,
  widgetData: PropTypes.object
};

export default CreateCustomWidget;
